class HeaderBravoCartDrawer extends HTMLElement {
  /** @type {HTMLElement[]} */
  #boundTriggers = [];

  /** @type {HTMLDialogElement | null} */
  #currentDialog = null;

  /** @type {HTMLElement | null} */
  #currentDrawer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#bindTriggers();
    this.#bindDialog();
    this.#bindDrawer();

    const signal = this.#abortController?.signal;
    if (signal) {
      this.addEventListener('click', this.#handleSellingPlanToggleDelegated, { signal });
      this.addEventListener('click', this.#handleUpsellAddDelegated, { signal });
      this.addEventListener('click', this.#handleBundleItemQuantityDelegated, { signal });
      this.addEventListener('change', this.#handleBundleItemQuantityInputDelegated, { signal });
      this.addEventListener('keydown', this.#handleBundleItemQuantityInputKeydownDelegated, { signal });
      this.addEventListener('focusin', this.#handleBundleItemQuantityInputFocusDelegated, { signal });
      this.addEventListener('bundle:quantity-error', this.#handleBundleQuantityError, { signal });
    }
  }

  disconnectedCallback() {
    for (const trigger of this.#boundTriggers) {
      trigger.removeEventListener('click', this.#handleTriggerClick);
    }

    this.#boundTriggers = [];
    this.#unbindDialog();
    this.#unbindDrawer();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Returns bundle metadata for the active cart bundle.
   * If multiple bundle groups are present, returns null to avoid ambiguous merges.
   * @returns {Promise<Record<string, string> | null>}
   */
  async #getActiveBundleProperties() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const bundleCandidates = items.filter((item) => {
        const bundleId = item?.properties?._bb_bundle_id;
        return typeof bundleId === 'string' && bundleId.trim() !== '';
      });

      if (!bundleCandidates.length) {
        return null;
      }

      const bundleIds = new Set(
        bundleCandidates
          .map((item) => String(item?.properties?._bb_bundle_id || '').trim())
          .filter(Boolean)
      );

      if (bundleIds.size !== 1) {
        return null;
      }

      const activeBundleProperties = bundleCandidates[0]?.properties || {};

      const normalizedBundleProperties = Object.entries(activeBundleProperties).reduce((accumulator, [key, value]) => {
        if (!key.startsWith('_bb_')) {
          return accumulator;
        }

        if (value === null || value === undefined) {
          return accumulator;
        }

        accumulator[key] = String(value);
        return accumulator;
      }, /** @type {Record<string, string>} */ ({}));

      return Object.keys(normalizedBundleProperties).length ? normalizedBundleProperties : null;
    } catch (error) {
      console.error('[HeaderBravoCartDrawer] Failed to resolve active bundle metadata', error);
      return null;
    }
  }

  #bindTriggers() {
    const host = this.closest('header-bravo-component');
    if (!(host instanceof HTMLElement)) return;

    const triggers = host.querySelectorAll('[data-header-bravo-cart-trigger]');
    for (const trigger of triggers) {
      if (!(trigger instanceof HTMLElement)) continue;

      trigger.removeEventListener('click', this.#handleTriggerClick);
      trigger.addEventListener('click', this.#handleTriggerClick);
      this.#boundTriggers.push(trigger);
    }

    const overlay = this.querySelector('[data-header-bravo-cart-overlay]');
    if (overlay instanceof HTMLElement) {
      overlay.addEventListener('click', () => {
        this.#closeDrawer();
      });
    }
  }

  #bindDialog() {
    const dialog = this.querySelector('dialog[ref="dialog"]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#unbindDialog();
    dialog.addEventListener('close', this.#syncTriggerState);
    dialog.addEventListener('close', this.#syncOverlayState);
    this.#currentDialog = dialog;
    this.#syncTriggerState();
    this.#syncOverlayState();
  }

  #unbindDialog() {
    if (!(this.#currentDialog instanceof HTMLDialogElement)) return;

    this.#currentDialog.removeEventListener('close', this.#syncTriggerState);
    this.#currentDialog.removeEventListener('close', this.#syncOverlayState);
    this.#currentDialog = null;
  }

  #bindDrawer() {
    const drawer = this.querySelector('cart-drawer-component[data-header-bravo-cart-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    this.#unbindDrawer();
    drawer.addEventListener('dialog:open', this.#syncTriggerState);
    drawer.addEventListener('dialog:open', this.#syncOverlayState);
    drawer.addEventListener('dialog:close', this.#syncTriggerState);
    drawer.addEventListener('dialog:close', this.#syncOverlayState);
    this.#currentDrawer = drawer;
  }

  #unbindDrawer() {
    if (!(this.#currentDrawer instanceof HTMLElement)) return;

    this.#currentDrawer.removeEventListener('dialog:open', this.#syncTriggerState);
    this.#currentDrawer.removeEventListener('dialog:open', this.#syncOverlayState);
    this.#currentDrawer.removeEventListener('dialog:close', this.#syncTriggerState);
    this.#currentDrawer.removeEventListener('dialog:close', this.#syncOverlayState);
    this.#currentDrawer = null;
  }

  /** @param {Event} event */
  #handleSellingPlanToggleDelegated = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-header-bravo-selling-plan-toggle]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    if (button.disabled) return;

    this.#handleSellingPlanToggle(button);
  };

  /** @param {HTMLButtonElement} button */
  #handleSellingPlanToggle = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    const cartItemsComponent = this.querySelector('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) return;

    const line = Number.parseInt(button.dataset.line || '', 10);
    const quantity = Number.parseInt(button.dataset.currentQuantity || '', 10);
    const bundleGroupId = String(button.dataset.bundleGroupId || '');

    if (!Number.isInteger(line) || !Number.isInteger(quantity)) return;

    const defaultSellingPlanId = Number.parseInt(button.dataset.defaultSellingPlanId || '', 10);
    const currentSellingPlanId = Number.parseInt(button.dataset.currentSellingPlanId || '', 10);
    const resolvedCurrentSellingPlanId = Number.isInteger(currentSellingPlanId) && currentSellingPlanId > 0
      ? currentSellingPlanId
      : null;
    const resolvedDefaultSellingPlanId = Number.isInteger(defaultSellingPlanId) && defaultSellingPlanId > 0
      ? defaultSellingPlanId
      : null;
    const sellingPlanId = resolvedCurrentSellingPlanId
      ? null
      : resolvedDefaultSellingPlanId;

    if (sellingPlanId === null && resolvedCurrentSellingPlanId === null) return;
    if (sellingPlanId === resolvedCurrentSellingPlanId) return;

    const container = button.closest('.header-bravo-cart-drawer__selling-plan');
    container?.classList.add('is-loading');
    button.disabled = true;

    let updatePromise = null;

    if (bundleGroupId && 'updateBundleSellingPlan' in cartItemsComponent && typeof cartItemsComponent.updateBundleSellingPlan === 'function') {
      updatePromise = cartItemsComponent.updateBundleSellingPlan({
        bundleGroupId,
        fallbackLine: line,
        sellingPlanId,
      });
    } else if ('updateLineItem' in cartItemsComponent && typeof cartItemsComponent.updateLineItem === 'function') {
      updatePromise = cartItemsComponent.updateLineItem({
        line,
        quantity,
        sellingPlanId,
        action: 'selling-plan-change',
      });
    }

    if (updatePromise && typeof updatePromise.finally === 'function') {
      updatePromise.finally(() => {
        container?.classList.remove('is-loading');
        button.disabled = false;
      });
      return;
    }

    container?.classList.remove('is-loading');
    button.disabled = false;
  };

  /** @param {Event} event */
  #handleUpsellAddDelegated = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest('[data-header-bravo-upsell-add]');
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    event.preventDefault();

    if (button.disabled) {
      return;
    }

    const variantId = Number.parseInt(button.dataset.variantId || '', 10);
    const productId = button.dataset.productId || '';
    const isBundleProduct = button.dataset.isBundleProduct === 'true';

    if (!Number.isFinite(variantId) || variantId <= 0) {
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const originalText = button.textContent;
    button.disabled = true;

    try {
      const bundleProperties = isBundleProduct ? await this.#getActiveBundleProperties() : null;
      const lineItem = {
        id: variantId,
        quantity: 1,
      };

      if (bundleProperties) {
        lineItem.properties = bundleProperties;
      }

      const payload = {
        items: [lineItem],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data?.status) {
        throw new Error(data.message || 'Unable to add upsell item');
      }

      const addedText = Theme?.translations?.added || 'Added';
      button.textContent = addedText;

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: String(variantId),
            data: {
              source: 'header-bravo-cart-drawer-upsell',
              productId,
              itemCount: 1,
              variantId: String(variantId),
              sections: data.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 900);
    }
  };

  /** @param {Event} event */
  #handleBundleItemQuantityDelegated = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest('[data-header-bravo-bundle-qty-control]');
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    event.preventDefault();
    if (button.disabled) {
      return;
    }

    const quantityContainer = button.closest('[data-header-bravo-bundle-qty]');
    if (!(quantityContainer instanceof HTMLElement)) {
      return;
    }

    this.#clearBundleQuantityError(quantityContainer);

    const cartItemsComponent = this.querySelector('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) {
      return;
    }

    const bundleGroupId = String(quantityContainer.dataset.bundleGroupId || '');
    const line = Number.parseInt(quantityContainer.dataset.line || '', 10);
    const lineKey = String(quantityContainer.dataset.lineKey || '');
    const bundleMinQty = Number.parseInt(quantityContainer.dataset.bundleMinQty || '', 10);
    const delta = Number.parseInt(button.dataset.delta || '', 10);

    if (!bundleGroupId || !Number.isInteger(line) || !Number.isInteger(delta) || delta === 0) {
      return;
    }

    let updatePromise = null;
    if ('updateBundleLineQuantity' in cartItemsComponent && typeof cartItemsComponent.updateBundleLineQuantity === 'function') {
      updatePromise = cartItemsComponent.updateBundleLineQuantity({
        bundleGroupId,
        line,
        lineKey,
        delta,
        bundleMinQty: Number.isInteger(bundleMinQty) ? bundleMinQty : 0,
      });
    }

    if (!updatePromise || typeof updatePromise.finally !== 'function') {
      return;
    }

    this.#toggleBundleQuantityLoading(quantityContainer, true);
    updatePromise.finally(() => {
      this.#toggleBundleQuantityLoading(quantityContainer, false);
    });
  };

  /** @param {Event} event */
  #handleBundleItemQuantityInputDelegated = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-header-bravo-bundle-qty-input]')) {
      return;
    }

    const quantityContainer = target.closest('[data-header-bravo-bundle-qty]');
    if (quantityContainer instanceof HTMLElement) {
      this.#clearBundleQuantityError(quantityContainer);
    }

    this.#submitBundleQuantityInput(target);
  };

  /** @param {KeyboardEvent} event */
  #handleBundleItemQuantityInputKeydownDelegated = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-header-bravo-bundle-qty-input]')) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    target.blur();
  };

  /** @param {FocusEvent} event */
  #handleBundleItemQuantityInputFocusDelegated = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-header-bravo-bundle-qty-input]')) {
      return;
    }

    const quantityContainer = target.closest('[data-header-bravo-bundle-qty]');
    if (quantityContainer instanceof HTMLElement) {
      this.#clearBundleQuantityError(quantityContainer);
    }

    target.select();
  };

  /** @param {Event} event */
  #handleBundleQuantityError = (event) => {
    const detail = /** @type {{ lineKey?: string, message?: string }} */ (event instanceof CustomEvent ? event.detail : {});
    const lineKey = String(detail?.lineKey || '');
    const message = String(detail?.message || '').trim();

    if (!lineKey || !message) {
      return;
    }

    const quantityContainer = this.querySelector(`[data-header-bravo-bundle-qty][data-line-key="${CSS.escape(lineKey)}"]`);
    if (!(quantityContainer instanceof HTMLElement)) {
      return;
    }

    const template = String(quantityContainer.dataset.bundleMinErrorTemplate || '').trim();
    const bundleMinQty = Number.parseInt(quantityContainer.dataset.bundleMinQty || '', 10);
    const resolvedMessage = template && Number.isInteger(bundleMinQty)
      ? template.replace('[minimum]', String(bundleMinQty))
      : message;

    this.#showBundleQuantityError(quantityContainer, resolvedMessage);
  };

  /** @param {HTMLInputElement} input */
  #submitBundleQuantityInput(input) {
    const quantityContainer = input.closest('[data-header-bravo-bundle-qty]');
    if (!(quantityContainer instanceof HTMLElement)) {
      return;
    }

    const cartItemsComponent = this.querySelector('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) {
      return;
    }

    const bundleGroupId = String(quantityContainer.dataset.bundleGroupId || '');
    const line = Number.parseInt(quantityContainer.dataset.line || '', 10);
    const lineKey = String(quantityContainer.dataset.lineKey || '');
    const bundleMinQty = Number.parseInt(quantityContainer.dataset.bundleMinQty || '', 10);
    const currentQuantity = Number.parseInt(input.dataset.currentQuantity || input.defaultValue || '', 10);
    const nextQuantity = Number.parseInt(input.value || '', 10);

    if (!Number.isInteger(nextQuantity) || nextQuantity < 0 || !bundleGroupId || !Number.isInteger(line)) {
      input.value = String(currentQuantity || 1);
      return;
    }

    if (nextQuantity === currentQuantity) {
      input.value = String(currentQuantity);
      return;
    }

    let updatePromise = null;
    if ('updateBundleLineQuantity' in cartItemsComponent && typeof cartItemsComponent.updateBundleLineQuantity === 'function') {
      updatePromise = cartItemsComponent.updateBundleLineQuantity({
        bundleGroupId,
        line,
        lineKey,
        quantity: nextQuantity,
        bundleMinQty: Number.isInteger(bundleMinQty) ? bundleMinQty : 0,
      });
    }

    if (!updatePromise || typeof updatePromise.finally !== 'function') {
      input.value = String(currentQuantity);
      return;
    }

    this.#toggleBundleQuantityLoading(quantityContainer, true);
    updatePromise.finally(() => {
      this.#toggleBundleQuantityLoading(quantityContainer, false);
      if (!document.body.contains(input)) {
        return;
      }

      input.value = String(input.dataset.currentQuantity || currentQuantity);
    });
  }

  /** @param {HTMLElement} quantityContainer @param {boolean} isLoading */
  #toggleBundleQuantityLoading(quantityContainer, isLoading) {
    quantityContainer.classList.toggle('is-loading', isLoading);

    const controls = quantityContainer.querySelectorAll('[data-header-bravo-bundle-qty-control]');
    controls.forEach((control) => {
      if (control instanceof HTMLButtonElement) {
        control.disabled = isLoading;
      }
    });

    const input = quantityContainer.querySelector('[data-header-bravo-bundle-qty-input]');
    if (input instanceof HTMLInputElement) {
      input.readOnly = isLoading;
    }
  }

  /** @param {HTMLElement} quantityContainer @param {string} message */
  #showBundleQuantityError(quantityContainer, message) {
    const errorContainer = quantityContainer.parentElement?.querySelector('[data-header-bravo-bundle-qty-error]');
    const errorText = errorContainer?.querySelector('[data-header-bravo-bundle-qty-error-text]');

    if (!(errorContainer instanceof HTMLElement) || !(errorText instanceof HTMLElement)) {
      return;
    }

    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
  }

  /** @param {HTMLElement} quantityContainer */
  #clearBundleQuantityError(quantityContainer) {
    const errorContainer = quantityContainer.parentElement?.querySelector('[data-header-bravo-bundle-qty-error]');
    const errorText = errorContainer?.querySelector('[data-header-bravo-bundle-qty-error-text]');

    if (!(errorContainer instanceof HTMLElement) || !(errorText instanceof HTMLElement)) {
      return;
    }

    errorText.textContent = '';
    errorContainer.classList.add('hidden');
  }

  /** @param {Event} event */
  #handleTriggerClick = (event) => {
    event.preventDefault();
    this.#closeMobileMenu();
    this.#openDrawer();
  };

  #closeMobileMenu() {
    const host = this.closest('header-bravo-component');
    if (!(host instanceof HTMLElement)) return;

    const toggleButton = host.querySelector('[data-header-bravo-toggle]');
    const panel = host.querySelector('[data-header-bravo-panel]');

    if (toggleButton instanceof HTMLElement) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }

    if (panel instanceof HTMLElement) {
      panel.hidden = true;
    }

    document.body.classList.remove('header-bravo-mobile-open');
  }

  #openDrawer() {
    const cartDrawer = this.querySelector('cart-drawer-component[data-header-bravo-cart-drawer]');
    if (!(cartDrawer instanceof HTMLElement)) return;

    if ('open' in cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      this.#syncTriggerState();
      this.#syncOverlayState();
    }
  }

  #closeDrawer() {
    const cartDrawer = this.querySelector('cart-drawer-component[data-header-bravo-cart-drawer]');
    if (!(cartDrawer instanceof HTMLElement)) return;

    if ('close' in cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }
  }

  #syncOverlayState = () => {
    const dialog = this.querySelector('dialog[ref="dialog"]');
    const overlay = this.querySelector('[data-header-bravo-cart-overlay]');
    if (!(overlay instanceof HTMLElement)) return;

    const isOpen = dialog instanceof HTMLDialogElement && dialog.open;
    overlay.classList.toggle('is-visible', isOpen);
  };

  #syncTriggerState = () => {
    const dialog = this.querySelector('dialog[ref="dialog"]');
    const isOpen = dialog instanceof HTMLDialogElement && dialog.open;

    for (const trigger of this.#boundTriggers) {
      trigger.setAttribute('aria-expanded', String(isOpen));
    }
  };
}

if (!customElements.get('header-bravo-cart-drawer')) {
  customElements.define('header-bravo-cart-drawer', HeaderBravoCartDrawer);
}
